import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { PostHogProvider } from "posthog-js/react";
import App from "./App.tsx";
import "./index.css";
import { initPostHog } from "./lib/posthog";

initPostHog();

const ROUTE_RECOVERY_KEY = "__vauren_route_recovery__";

const isLazyImportFailure = (reason: unknown) => {
  const message =
    typeof reason === "string"
      ? reason
      : reason instanceof Error
        ? reason.message
        : typeof reason === "object" && reason && "message" in reason
          ? String((reason as { message?: unknown }).message)
          : "";

  return message.includes("Failed to fetch dynamically imported module");
};

const recoverPreviewRoute = () => {
  if (sessionStorage.getItem(ROUTE_RECOVERY_KEY) === "1") return;
  sessionStorage.setItem(ROUTE_RECOVERY_KEY, "1");
  window.location.reload();
};

if (sessionStorage.getItem(ROUTE_RECOVERY_KEY) === "1") {
  sessionStorage.removeItem(ROUTE_RECOVERY_KEY);
}

window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  recoverPreviewRoute();
});

window.addEventListener("unhandledrejection", (event) => {
  if (!isLazyImportFailure(event.reason)) return;
  event.preventDefault();
  recoverPreviewRoute();
});

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const hostname = window.location.hostname;
const isPreviewHost = hostname.includes("lovableproject.com") || hostname.includes("id-preview--");
const shouldEnableSentry = import.meta.env.PROD && !isPreviewHost && Boolean(sentryDsn);

const resetPreviewRuntimeCaches = async () => {
  if ("serviceWorker" in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  }

  if ("caches" in window) {
    const cacheKeys = await caches.keys();
    await Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey)));
  }
};

if (isPreviewHost) {
  resetPreviewRuntimeCaches().catch(() => {});
}

if (shouldEnableSentry) {
  Sentry.init({
    dsn: sentryDsn,
    enabled: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.MODE,
  });
}

createRoot(document.getElementById("root")!).render(
  <PostHogProvider apiKey={import.meta.env.VITE_POSTHOG_KEY} options={{ api_host: import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com" }}>
    <App />
  </PostHogProvider>
);

// Register service worker for PWA support
if ('serviceWorker' in navigator && import.meta.env.PROD && !isPreviewHost) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).catch(() => {});
  });
}
